import type { IPeerConnection } from '../../types';
import type { ITuningClock } from '../contracts/ports';
import type { TuningCapabilities } from '../contracts/settings';
import { TUNING_CONTROL_MAX_BYTES as CONTROL_MAX_BYTES, TUNING_TELEMETRY_MAX_BYTES as TELEMETRY_MAX_BYTES,
  TUNING_TELEMETRY_BUFFER_MAX_BYTES as TELEMETRY_BUFFER_MAX_BYTES, TUNING_ENDPOINT_BYTES_PER_SECOND as ENDPOINT_BYTES_PER_SECOND,
  TUNING_CONTROL_QUEUE_MAX as CONTROL_QUEUE_LIMIT } from '../contracts/measurements';
import type { TuningTelemetryWindow } from '../contracts/measurements';
import type { TuningWireMessage } from '../contracts/protocol';
import { parseTuningWireMessage } from '../contracts/protocol';
import { TelemetryPendingWindow } from './TelemetryPendingWindow';

export const TUNING_CONTROL_CHANNEL = 'stream-tuning-control-v1';
export const TUNING_TELEMETRY_CHANNEL = 'stream-tuning-telemetry-v1';
const CONTROL_QUEUE_TTL_MS = 2_000;

export type TuningChannelServiceOptions = {
  role: 'host' | 'guest'; connection: Pick<IPeerConnection, 'getRtcPeerConnection'>;
  capabilities: () => TuningCapabilities; clock: ITuningClock;
  /** Captures the native connection identity; false after any generation replacement. */
  isCurrent: () => boolean;
  /** Current authenticated session/peer/epoch/host lease authority, including prepared transitions. */
  authorize: (message: TuningWireMessage) => boolean;
  onMessage: (message: TuningWireMessage) => void;
  onRejected?: (reason: string, message?: TuningWireMessage) => void;
};
type QueuedControl = { message: TuningWireMessage; json: string; bytes: number; expiresAt: number;
  state: 'sent' | 'queued' | 'rejected' };
export type TuningChannelSnapshot = {
  available: boolean; capabilitiesAccepted: boolean;
  controlState?: RTCDataChannelState; telemetryState?: RTCDataChannelState;
  controlQueued: number; telemetryPending: boolean;
  sentTelemetryBytes: number; receivedTelemetryBytes: number;
  telemetryDropped: number; histogramSamplesDropped: number; rejected: number;
};

/** Owns diagnostic native channels only. Never touches peer-lite's input/control routing. */
export class TuningChannelService {
  private readonly pc: RTCPeerConnection | null;
  private control: RTCDataChannel | undefined;
  private telemetry: RTCDataChannel | undefined;
  private readonly unbind: Array<() => void> = [];
  private readonly queue: QueuedControl[] = [];
  private readonly pending: TelemetryPendingWindow;
  private retryTimer: ReturnType<typeof setTimeout> | undefined;
  private closed = false;
  private available = false;
  private helloSent = false;
  private helloReceived = false;
  private outgoingTokens = ENDPOINT_BYTES_PER_SECOND;
  private incomingTokens = ENDPOINT_BYTES_PER_SECOND;
  private tokenAtMs: number;
  private sentTelemetryBytes = 0;
  private receivedTelemetryBytes = 0;
  private droppedTelemetry = 0;
  private rejected = 0;

  constructor(private readonly options: TuningChannelServiceOptions) {
    this.tokenAtMs = options.clock.monotonicMs();
    this.pending = new TelemetryPendingWindow(window => this.bytes(this.envelope(window)) <= TELEMETRY_MAX_BYTES);
    this.pc = options.connection.getRtcPeerConnection();
    if (!this.pc || !options.capabilities().eligible || !options.isCurrent()) return;
    this.available = true;
    if (options.role === 'guest') {
      try {
        this.attach(this.pc.createDataChannel(TUNING_CONTROL_CHANNEL, { ordered: true }));
        this.attach(this.pc.createDataChannel(TUNING_TELEMETRY_CHANNEL, { ordered: false, maxRetransmits: 0 }));
      } catch { this.dispose(); this.available = false; this.reject('native-channels-unavailable'); }
    } else {
      const listener = (event: RTCDataChannelEvent) => { if (this.current()) this.attach(event.channel); };
      this.pc.addEventListener('datachannel', listener);
      this.unbind.push(() => this.pc?.removeEventListener('datachannel', listener));
    }
  }

  send(message: TuningWireMessage): 'sent' | 'queued' | 'rejected' {
    // Queue the validated value, not a caller-owned object that can change behind authorization.
    let normalized: unknown;
    try { normalized = JSON.parse(JSON.stringify(message)); } catch { return this.reject('invalid-or-stale', message); }
    if (!this.current() || !parseTuningWireMessage(normalized)) return this.reject('invalid-or-stale', message);
    message = normalized as TuningWireMessage;
    if (message.kind === 'hello') return this.sendHello(message);
    if (!this.capabilitiesAccepted() || !this.authorized(message)) return this.reject('not-authorized', message);
    if (message.kind === 'telemetry') return this.sendTelemetry(message);
    const json = JSON.stringify(message), bytes = this.bytes(message);
    if (bytes > CONTROL_MAX_BYTES || this.queue.length >= CONTROL_QUEUE_LIMIT) return this.reject('control-limit', message);
    const item: QueuedControl = { message, json, bytes, expiresAt: this.options.clock.monotonicMs() + CONTROL_QUEUE_TTL_MS, state: 'queued' };
    this.queue.push(item);
    this.flush();
    return item.state;
  }

  capabilitiesAccepted(): boolean { return this.helloSent && this.helloReceived && this.current(); }

  flush(): void {
    if (!this.current()) { this.dispose(); return; }
    if (this.control?.readyState === 'open' && !this.helloSent) this.hello();
    this.flushControl();
    this.flushTelemetry();
    if ((this.queue.length || this.pending.peek() || (this.control?.readyState === 'open' && !this.helloSent)) && !this.retryTimer) {
      this.retryTimer = setTimeout(() => { this.retryTimer = undefined; this.flush(); }, 250);
    }
  }

  snapshot(): TuningChannelSnapshot {
    const pending = this.pending.snapshot();
    return { available: this.available && !this.closed, capabilitiesAccepted: this.capabilitiesAccepted(),
      controlState: this.control?.readyState, telemetryState: this.telemetry?.readyState,
      controlQueued: this.queue.length, telemetryPending: !!this.pending.peek(),
      sentTelemetryBytes: this.sentTelemetryBytes, receivedTelemetryBytes: this.receivedTelemetryBytes,
      telemetryDropped: this.droppedTelemetry + pending.coalesced,
      histogramSamplesDropped: pending.histogramSamplesDropped, rejected: this.rejected };
  }

  dispose(): void {
    if (this.closed) return;
    this.closed = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = undefined;
    for (const remove of this.unbind.splice(0)) remove();
    for (const item of this.queue.splice(0)) { item.state = 'rejected'; this.reject('disposed', item.message); }
    this.pending.discard();
    this.control?.close(); this.telemetry?.close();
  }

  private current(): boolean {
    try { return !this.closed && this.available && this.options.capabilities().eligible && this.options.isCurrent(); }
    catch { return false; }
  }

  private authorized(message: TuningWireMessage): boolean {
    try { return this.options.authorize(message); } catch { return false; }
  }

  private attach(channel: RTCDataChannel): void {
    const control = channel.label === TUNING_CONTROL_CHANNEL;
    if (!control && channel.label !== TUNING_TELEMETRY_CHANNEL) return;
    const existing = control ? this.control : this.telemetry;
    if (!this.current() || (existing && existing !== channel) || !this.correctDelivery(channel, control)) {
      channel.close(); this.reject('channel-binding-or-delivery'); return;
    }
    if (existing === channel) return;
    if (control) this.control = channel; else this.telemetry = channel;
    channel.bufferedAmountLowThreshold = control ? 0 : TELEMETRY_BUFFER_MAX_BYTES / 2;
    const opened = () => { if (this.current()) { if (control) this.hello(); this.flush(); } };
    const incoming = (event: MessageEvent) => this.receive(channel, event.data);
    const low = () => { if (this.current()) this.flush(); };
    channel.addEventListener('open', opened);
    channel.addEventListener('message', incoming);
    channel.addEventListener('bufferedamountlow', low);
    this.unbind.push(() => { channel.removeEventListener('open', opened); channel.removeEventListener('message', incoming);
      channel.removeEventListener('bufferedamountlow', low); });
    if (channel.readyState === 'open') opened();
  }

  private correctDelivery(channel: RTCDataChannel, control: boolean): boolean {
    return control ? channel.ordered && channel.maxRetransmits === null && channel.maxPacketLifeTime === null
      : !channel.ordered && channel.maxRetransmits === 0 && channel.maxPacketLifeTime === null;
  }

  private hello(): void {
    if (!this.helloSent) this.sendHello({ type: 'stream-tuning', version: 1, kind: 'hello', payload: this.options.capabilities() });
  }

  private sendHello(message: TuningWireMessage & { kind: 'hello' }): 'sent' | 'rejected' {
    if (!parseTuningWireMessage(message) || this.helloSent || message.payload.endpoint !== this.options.role || !message.payload.eligible
      || !this.write(this.control, JSON.stringify(message), this.bytes(message), CONTROL_MAX_BYTES)) return this.reject('hello-unavailable', message);
    this.helloSent = true;
    return 'sent';
  }

  private receive(channel: RTCDataChannel, raw: unknown): void {
    if (!this.current() || typeof raw !== 'string' || new TextEncoder().encode(raw).length > CONTROL_MAX_BYTES) return;
    let parsed: unknown;
    try { parsed = JSON.parse(raw); } catch { this.reject('malformed-json'); return; }
    const message = parseTuningWireMessage(parsed);
    if (!message || (message.kind === 'telemetry') !== (channel === this.telemetry)) { this.reject('invalid-channel-message'); return; }
    if (message.kind === 'hello') {
      if (this.helloReceived || !message.payload.eligible || message.payload.endpoint === this.options.role) { this.reject('invalid-hello'); return; }
      this.helloReceived = true;
    } else {
      if (!this.capabilitiesAccepted() || !this.authorized(message)) { this.reject('not-authorized', message); return; }
      if (message.kind === 'telemetry') {
        if (message.payload.endpoint === this.options.role) { this.reject('wrong-telemetry-endpoint', message); return; }
        this.refill(); const bytes = new TextEncoder().encode(raw).length;
        if (bytes > this.incomingTokens) { this.droppedTelemetry++; this.reject('incoming-byte-budget', message); return; }
        this.incomingTokens -= bytes; this.receivedTelemetryBytes += bytes;
      }
    }
    try { this.options.onMessage(message); } catch { this.reject('handler-failed', message); }
    this.flush();
  }

  private sendTelemetry(message: TuningWireMessage & { kind: 'telemetry' }): 'sent' | 'queued' | 'rejected' {
    if (message.payload.endpoint !== this.options.role || this.bytes(message) > TELEMETRY_MAX_BYTES) return this.reject('telemetry-endpoint-or-limit', message);
    const sentBefore = this.sentTelemetryBytes;
    this.pending.push(message.payload);
    this.flush();
    return this.pending.peek() ? 'queued' : this.sentTelemetryBytes > sentBefore ? 'sent' : 'rejected';
  }

  private flushControl(): void {
    while (this.queue.length) {
      const item = this.queue[0];
      if (item.expiresAt <= this.options.clock.monotonicMs() || !this.authorized(item.message)) {
        item.state = 'rejected'; this.queue.shift(); this.reject('control-expired-or-revoked', item.message); continue;
      }
      if (!this.write(this.control, item.json, item.bytes, CONTROL_MAX_BYTES)) return;
      item.state = 'sent';
      this.queue.shift();
    }
  }

  private flushTelemetry(): void {
    const window = this.pending.peek();
    if (!window || !this.capabilitiesAccepted()) return;
    const message = this.envelope({ ...window, telemetryDropped: window.telemetryDropped + this.snapshot().telemetryDropped });
    if (!this.authorized(message)) { this.pending.discard(); this.reject('telemetry-revoked', message); return; }
    const bytes = this.bytes(message); this.refill();
    if (bytes > TELEMETRY_MAX_BYTES) { this.pending.discard(); this.reject('coalesced-envelope-limit', message); return; }
    if (bytes > this.outgoingTokens || !this.write(this.telemetry, JSON.stringify(message), bytes, TELEMETRY_BUFFER_MAX_BYTES)) return;
    this.outgoingTokens -= bytes; this.sentTelemetryBytes += bytes; this.pending.take();
  }

  private refill(): void {
    const now = this.options.clock.monotonicMs(), elapsed = Math.max(0, now - this.tokenAtMs);
    const tokens = elapsed * ENDPOINT_BYTES_PER_SECOND / 1_000;
    this.outgoingTokens = Math.min(ENDPOINT_BYTES_PER_SECOND, this.outgoingTokens + tokens);
    this.incomingTokens = Math.min(ENDPOINT_BYTES_PER_SECOND, this.incomingTokens + tokens);
    this.tokenAtMs = Math.max(this.tokenAtMs, now);
  }

  private write(channel: RTCDataChannel | undefined, json: string, bytes: number, maxBuffered: number): boolean {
    if (!channel || channel.readyState !== 'open' || !Number.isFinite(channel.bufferedAmount)
      || channel.bufferedAmount < 0 || channel.bufferedAmount + bytes > maxBuffered) return false;
    try { channel.send(json); return true; } catch { return false; }
  }

  private envelope(payload: TuningTelemetryWindow): TuningWireMessage & { kind: 'telemetry' } {
    return { type: 'stream-tuning', version: 1, kind: 'telemetry', payload };
  }

  private bytes(message: unknown): number { return new TextEncoder().encode(JSON.stringify(message)).length; }
  private reject(reason: string, message?: TuningWireMessage): 'rejected' {
    this.rejected++;
    try { this.options.onRejected?.(reason, message); } catch { /* Observers cannot break channel cleanup. */ }
    return 'rejected';
  }
}
